import clsx from 'clsx';
import React from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { HeroIcon } from '../../dashboard/Dashboard.types';

// #region SettingsSectionInterface
export interface SettingsSection {
    /**
     * Stable url-safe identifier. Written to the query string (or the path, in
     * `basePath` mode), so never reuse or rename casually. May contain slashes
     * for nested sections, e.g. `inventory/vendors`.
     */
    key: string;
    /** Label shown in the left navigation. */
    label: string;
    /** Optional leading icon. */
    icon?: HeroIcon;
    /** Optional secondary line under the label. */
    description?: string;
    /**
     * Lazily invoked so only the active section mounts. Omitted in `basePath`
     * mode, where the routed child is rendered through an `<Outlet />` instead.
     */
    render?: () => React.ReactNode;
}

// #region SettingsLayoutInterface
export interface SettingsLayout {
    /** Ordered, already permission-filtered sections. */
    sections: SettingsSection[];
    /** Optional heading rendered above the two columns. */
    title?: string;
    /** Optional sub-heading rendered under the title. */
    subtitle?: string;
    /** Query string parameter used to persist the active section. Defaults to `tab`. */
    paramName?: string;
    /**
     * Opt into PATH-based sections: the active section is read from the path
     * segment(s) after `basePath` and selecting one navigates to
     * `${basePath}/${key}`, with the routed child rendered via `<Outlet />`.
     *
     * This is what lets a section own sub-pages (`/admin-tools/mockups/12`)
     * without losing the left navigation. `/settings` and `/release-notes` stay
     * on the `?tab=` behaviour.
     */
    basePath?: string;
}

export type SettingsLayoutType = SettingsLayout & Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>;

// #region SettingsLayout
export const SettingsLayout: React.FunctionComponent<SettingsLayoutType> = (props: SettingsLayoutType): JSX.Element => {
    const { sections, title, subtitle, paramName = 'tab', basePath, className, ...remaining } = props;

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // #region resolveActiveKey
    /**
     * In path mode the remainder after `basePath` can be deeper than the section
     * key (`inventory/trackable-items/9` for section `inventory/trackable-items`),
     * so the LONGEST matching key wins — otherwise `inventory` would swallow all
     * of its own sub-sections and highlight the wrong nav row.
     */
    const requestedKey = React.useMemo<string | null>(() => {
        if (!basePath) return searchParams.get(paramName);

        const remainder = pathname.startsWith(basePath) ? pathname.slice(basePath.length).replace(/^\//, '') : '';
        if (!remainder) return null;

        return sections
            .filter((section: SettingsSection) => remainder === section.key || remainder.startsWith(`${section.key}/`))
            .sort((a: SettingsSection, b: SettingsSection) => b.key.length - a.key.length)[0]?.key ?? null;
    }, [basePath, pathname, searchParams, paramName, sections]);

    // An unknown or permission-denied key silently falls back to the first available
    // section rather than rendering an empty pane.
    const activeSection = sections.find((section: SettingsSection) => section.key === requestedKey) ?? sections[0];

    // #region selectSection
    const selectSection = (key: string): void => {
        if (basePath) {
            navigate(`${basePath}/${key}`);
            return;
        }

        const next = new URLSearchParams(searchParams);
        next.set(paramName, key);
        setSearchParams(next, { replace: false });
    };

    if (sections.length === 0) return <></>;

    return (
        <div className={clsx(className, 'flex flex-col gap-y-6')} {...remaining}>
            {
                (title || subtitle) &&
                <div className="flex flex-col gap-y-1">
                    {title && <h1 className="text-xl font-semibold text-zinc-900 dark:text-white">{title}</h1>}
                    {subtitle && <p className="text-sm text-zinc-500 dark:text-zinc-400">{subtitle}</p>}
                </div>
            }

            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                {/* Mobile / tablet section picker */}
                <div className="lg:hidden">
                    <label htmlFor="settings-section-select" className="sr-only">Select a section</label>
                    <select
                        id="settings-section-select"
                        value={activeSection.key}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => selectSection(event.target.value)}
                        className={clsx(
                            'block w-full rounded-lg border px-3 py-2 text-sm outline-none',
                            'border-zinc-950/10 bg-surface-raised text-zinc-900',
                            'dark:border-white/10 dark:text-white',
                            'focus:ring-2 focus:ring-sky-500',
                        )}
                    >
                        {
                            sections.map((section: SettingsSection) => (
                                <option key={section.key} value={section.key}>{section.label}</option>
                            ))
                        }
                    </select>
                </div>

                {/* Desktop left navigation */}
                <nav
                    aria-label={title ?? 'Sections'}
                    className={clsx(
                        'hidden lg:flex lg:flex-col lg:shrink-0 lg:w-64 gap-y-0.5',
                        'lg:sticky lg:top-2 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto',
                        'lg:border-r lg:border-zinc-200 dark:lg:border-zinc-700 lg:pr-4',
                    )}
                >
                    {
                        sections.map((section: SettingsSection) => {
                            const isActive = section.key === activeSection.key;
                            const Icon: HeroIcon | undefined = section.icon;

                            return (
                                <button
                                    key={section.key}
                                    type="button"
                                    aria-current={isActive ? 'page' : undefined}
                                    onClick={() => selectSection(section.key)}
                                    className={clsx(
                                        'group flex items-start gap-2.5 w-full rounded-lg px-3 py-2 text-left text-sm transition-colors outline-none',
                                        isActive
                                            ? 'bg-sky-50 text-sky-700 font-semibold dark:bg-sky-900/30 dark:text-sky-300'
                                            : 'text-zinc-600 font-medium hover:bg-surface-muted hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white',
                                    )}
                                >
                                    {
                                        Icon &&
                                        <Icon
                                            className={clsx(
                                                'h-4 w-4 mt-0.5 shrink-0 transition-colors',
                                                isActive
                                                    ? 'text-sky-600 dark:text-sky-400'
                                                    : 'text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-600 dark:group-hover:text-zinc-300',
                                            )}
                                        />
                                    }
                                    <span className="flex flex-col gap-y-0.5 min-w-0">
                                        <span className="truncate">{section.label}</span>
                                        {
                                            section.description &&
                                            <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">
                                                {section.description}
                                            </span>
                                        }
                                    </span>
                                </button>
                            );
                        })
                    }
                </nav>

                {/* Content pane */}
                <div className="flex-1 min-w-0">
                    {basePath ? <Outlet /> : activeSection.render?.()}
                </div>
            </div>
        </div>
    );
};
